import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AlertTriangle,
    Check,
    FlaskConical,
    GitCompare,
    Loader2,
    Maximize2,
    RefreshCw,
    ShieldAlert,
    ShieldCheck,
    X,
} from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/Dialog";
import { isCapabilityEnabled } from "../../config/capabilities";
import { useToast } from "../../hooks/use-toast";
import { templatePath } from "../Templates/templateUrls";
import {
    supportContextCardTitle,
    supportInsetPanelAuto,
    supportSubpanelHeader,
} from "./supportStyles";
import {
    checkTemplateGuardrails,
    createTemplateDraft,
    fetchTemplateEditAccess,
    runTestNoteWithPrompt,
    saveTemplateEdit,
} from "./templateEditApi";
import TemplateTestNoteCompareModal from "./TemplateTestNoteCompareModal";

const TARGET_LABELS = {
    conversation_prompt: "Conversation prompt",
};

// In the inline card, long stretches of untouched template are collapsed so the
// changed lines stay on screen. The full-template modal never collapses.
const CONTEXT_COLLAPSE_THRESHOLD = 8;
const CONTEXT_EDGE_LINES = 3;

/**
 * Split the flat diff rows into alternating runs of context and changed lines.
 * Each changed line is accepted or declined on its own; the runs only exist so
 * context can be collapsed and changed lines can be counted.
 */
const groupDiffRows = (diff) => {
    const blocks = [];
    (diff || []).forEach((row, index) => {
        const kind = row.type === "context" ? "context" : "change";
        const last = blocks[blocks.length - 1];
        if (last && last.kind === kind) {
            last.rows.push({ ...row, index });
        } else {
            blocks.push({ kind, rows: [{ ...row, index }] });
        }
    });
    return blocks.map((block) => ({
        ...block,
        key: String(block.rows[0].index),
    }));
};

const ProviderImpact = ({ impact, specialty, target }) => {
    if (!impact) return null;

    if (impact.error) {
        return (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span>{impact.error}</span>
            </p>
        );
    }

    const count = impact.affected_provider_count;
    const targetLabel = (TARGET_LABELS[target] || "template").toLowerCase();

    if (count === null || count === undefined) {
        return (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span>
                    The number of affected providers could not be determined.
                    {impact.note ? ` ${impact.note}` : ""}
                </span>
            </p>
        );
    }

    return (
        <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-1">
            <p className="flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" aria-hidden />
                <span>
                    This edit would affect{" "}
                    <span className="font-semibold">
                        {count} provider{count === 1 ? "" : "s"}
                    </span>
                    {impact.total_providers_in_specialty != null && (
                        <>
                            {" "}
                            of {impact.total_providers_in_specialty} in{" "}
                            {specialty}
                        </>
                    )}
                    .{" "}
                    {impact.is_default
                        ? `It is the default ${targetLabel} for ${specialty}.`
                        : `It is assigned to a specific route in the ${specialty} workflow.`}
                </span>
            </p>
            {impact.note && (
                <p className="text-xs text-amber-800/90 pl-[1.375rem]">
                    {impact.note}
                </p>
            )}
        </div>
    );
};

// A decided line shows its outcome instead of the diff colors: "kept" text
// stays in the template, "dropped" text does not.
const rowTone = (row, resolution) => {
    if (resolution === "kept") return "bg-white text-gray-800";
    if (resolution === "dropped") return "bg-gray-50 text-gray-400";
    if (row.type === "context") return "bg-white text-gray-600";
    return row.type === "add"
        ? "bg-green-50 text-green-900"
        : "bg-red-50 text-red-900";
};

const rowMarker = (row) =>
    row.type === "context" ? " " : row.type === "add" ? "+" : "−";

const DiffRow = ({ row, resolution = null, children = null }) => (
    <div
        className={`flex items-start gap-2 py-0.5 pl-2 pr-2 border-b border-gray-100 last:border-b-0 ${rowTone(
            row,
            resolution,
        )}`}
    >
        <span className="w-4 shrink-0 text-center font-mono text-xs select-none">
            {rowMarker(row)}
        </span>
        <pre
            className={`flex-1 min-w-0 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words ${
                resolution === "dropped" ? "line-through" : ""
            }`}
        >
            {row.value || " "}
        </pre>
        {children}
    </div>
);

const decisionButtonBase =
    "inline-flex items-center gap-1 rounded-md border bg-white px-2 py-0.5 text-[11px] font-semibold shadow-sm transition-colors";

/**
 * One changed line. Undecided until the reviewer picks Accept or Decline;
 * after that it shows the outcome with an Undo.
 */
const ChangeRow = ({ row, decision, onDecide }) => {
    const what = row.type === "add" ? "added line" : "removed line";
    const resolution = !decision
        ? null
        : (decision === "accepted") === (row.type === "add")
          ? "kept"
          : "dropped";

    return (
        <DiffRow row={row} resolution={resolution}>
            {decision ? (
                <div className="shrink-0 self-start flex items-center gap-2 text-[11px] font-semibold">
                    <span
                        className={`inline-flex items-center gap-1 ${
                            decision === "accepted"
                                ? "text-green-700"
                                : "text-gray-500"
                        }`}
                    >
                        {decision === "accepted" ? (
                            <Check className="h-3 w-3" aria-hidden />
                        ) : (
                            <X className="h-3 w-3" aria-hidden />
                        )}
                        {decision === "accepted" ? "Accepted" : "Declined"}
                    </span>
                    <button
                        type="button"
                        onClick={() => onDecide(null)}
                        title={`Undo the decision on this ${what}`}
                        className="font-medium text-gray-500 hover:text-ktx-purple hover:underline"
                    >
                        Undo
                    </button>
                </div>
            ) : (
                <div className="shrink-0 self-start flex items-center gap-1">
                    <button
                        type="button"
                        onClick={() => onDecide("declined")}
                        title={`Decline this ${what}`}
                        className={`${decisionButtonBase} border-gray-300 text-gray-600 hover:bg-gray-100`}
                    >
                        <X className="h-3 w-3" aria-hidden />
                        Decline
                    </button>
                    <button
                        type="button"
                        onClick={() => onDecide("accepted")}
                        title={`Accept this ${what}`}
                        className={`${decisionButtonBase} border-green-600 text-green-700 hover:bg-green-600 hover:text-white`}
                    >
                        <Check className="h-3 w-3" aria-hidden />
                        Accept
                    </button>
                </div>
            )}
        </DiffRow>
    );
};

const ContextBlock = ({ rows, collapsible }) => {
    const [expanded, setExpanded] = useState(false);

    if (!collapsible || expanded || rows.length <= CONTEXT_COLLAPSE_THRESHOLD) {
        return (
            <>
                {rows.map((row) => (
                    <DiffRow key={row.index} row={row} />
                ))}
            </>
        );
    }

    const head = rows.slice(0, CONTEXT_EDGE_LINES);
    const tail = rows.slice(-CONTEXT_EDGE_LINES);
    const hidden = rows.length - head.length - tail.length;

    return (
        <>
            {head.map((row) => (
                <DiffRow key={row.index} row={row} />
            ))}
            <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full px-2 py-1 text-left text-xs text-gray-500 bg-slate-50 border-b border-gray-100 hover:text-ktx-purple hover:bg-slate-100"
            >
                Show {hidden} unchanged line{hidden === 1 ? "" : "s"}
            </button>
            {tail.map((row) => (
                <DiffRow key={row.index} row={row} />
            ))}
        </>
    );
};

/**
 * The diff itself. Rendered twice against the same decision state: compact in
 * the recommendation card, and in full in the View full template modal.
 */
const DiffReview = ({ blocks, decisions, onDecide, collapseContext }) =>
    blocks.map((block) =>
        block.kind === "context" ? (
            <ContextBlock
                key={`context-${block.key}`}
                rows={block.rows}
                collapsible={collapseContext}
            />
        ) : (
            block.rows.map((row) => (
                <ChangeRow
                    key={`change-${row.index}`}
                    row={row}
                    decision={decisions.get(row.index)}
                    onDecide={(decision) => onDecide(row.index, decision)}
                />
            ))
        ),
    );

const MetaChip = ({ label, children }) => (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 max-w-full">
        <span className="text-gray-500">{label}</span>
        <span className="font-medium text-gray-800 min-w-0">{children}</span>
    </span>
);

const DecisionCount = ({ accepted, total }) => (
    <span className="font-normal text-gray-500">
        {`(${accepted} of ${total} line${total === 1 ? "" : "s"} accepted)`}
    </span>
);

const BulkDecisionButtons = ({ onAcceptAll, onDeclineAll }) => (
    <div className="flex items-center gap-3 text-xs font-medium">
        <button
            type="button"
            onClick={onAcceptAll}
            className="text-ktx-purple hover:underline"
        >
            Accept all
        </button>
        <button
            type="button"
            onClick={onDeclineAll}
            className="text-gray-500 hover:text-gray-700 hover:underline"
        >
            Decline all
        </button>
    </div>
);

/** The stored text with only the accepted additions and declined removals kept. */
const buildReviewedText = (diff, decisions) =>
    (diff || [])
        .filter((row, index) => {
            if (row.type === "context") return true;
            const accepted = decisions.get(index) === "accepted";
            return row.type === "add" ? accepted : !accepted;
        })
        .map((row) => row.value)
        .join("\n");

const humanizeRule = (rule) =>
    String(rule || "rule").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const GuardrailResult = ({ review }) => {
    if (review.stage === "check_error") {
        return (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span>
                    No guardrail decision was made: {review.error} Nothing was
                    approved — check again.
                </span>
            </p>
        );
    }

    const { decision } = review;
    if (!decision) return null;

    if (decision.accepted) {
        return (
            <p className="text-sm text-green-900 bg-green-50 border border-green-200 rounded-md px-3 py-2 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />
                <span className="font-semibold">Guardrails passed.</span>
            </p>
        );
    }

    return (
        <div className="text-sm text-red-900 bg-red-50 border border-red-200 rounded-md px-3 py-2 space-y-1.5">
            <p className="flex items-start gap-1.5">
                <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                <span>
                    <span className="font-semibold">Guardrails rejected the edit.</span>{" "}
                    {decision.reason}
                </span>
            </p>
            {decision.violations.length > 0 && (
                <ul className="pl-[1.375rem] space-y-1 list-disc list-inside">
                    {decision.violations.map((violation, index) => (
                        <li key={index}>
                            <span className="font-medium">
                                {humanizeRule(violation.rule)}
                            </span>
                            {Array.isArray(violation.change_ids) &&
                                violation.change_ids.length > 0 && (
                                    <span className="text-red-700/80">
                                        {" "}
                                        ({violation.change_ids.join(", ")})
                                    </span>
                                )}
                            {violation.detail ? `: ${violation.detail}` : ""}
                        </li>
                    ))}
                </ul>
            )}
            <p className="pl-[1.375rem] text-xs text-red-800/90">
                Decline the lines causing this and check again.
            </p>
        </div>
    );
};

/**
 * Review surface for the update_template recommendation: who the change hits,
 * the proposed edits as accept/decline diff lines, then guardrail check →
 * test notes with the current and suggested prompt → save.
 */
const UpdateTemplateAction = ({ payload = {}, patientId = null }) => {
    const { toast } = useToast();
    const [decisions, setDecisions] = useState(() => new Map());
    const [fullViewOpen, setFullViewOpen] = useState(false);
    const [review, setReview] = useState({ stage: "idle" });
    const [compareOpen, setCompareOpen] = useState(false);
    const [saveState, setSaveState] = useState(null);
    const [patientPromptOpen, setPatientPromptOpen] = useState(false);
    const [enteredPatientId, setEnteredPatientId] = useState("");
    const [editAccess, setEditAccess] = useState({ status: "loading" });
    const accessRequestRef = useRef(0);
    const draftRef = useRef(null);
    // Each check/test run gets an id; changing a decision or unmounting bumps
    // it, so results from a stale run are dropped instead of overwriting state.
    const runIdRef = useRef(0);

    useEffect(
        () => () => {
            runIdRef.current += 1;
        },
        [],
    );

    const loadEditAccess = useCallback(async () => {
        const requestId = ++accessRequestRef.current;
        setEditAccess((prev) =>
            prev.status === "error"
                ? { ...prev, retrying: true }
                : { status: "loading" },
        );
        const result = await fetchTemplateEditAccess();
        if (requestId !== accessRequestRef.current) return;
        setEditAccess(
            result.error
                ? { status: "error", message: result.error }
                : { status: result.allowed ? "allowed" : "denied" },
        );
    }, []);

    useEffect(() => {
        loadEditAccess();
        return () => {
            accessRequestRef.current += 1;
        };
    }, [loadEditAccess]);

    const blocks = useMemo(() => groupDiffRows(payload.diff), [payload.diff]);
    const changeKeys = useMemo(
        () =>
            blocks
                .filter((block) => block.kind === "change")
                .flatMap((block) => block.rows.map((row) => row.index)),
        [blocks],
    );

    const totalChanges = changeKeys.length;
    const acceptedChanges = changeKeys.filter(
        (key) => decisions.get(key) === "accepted",
    ).length;
    const defaultMoved = payload.default_moved === true;
    const locked = defaultMoved || editAccess.status !== "allowed";
    const canApply = acceptedChanges > 0 && !locked;
    const targetLabel = TARGET_LABELS[payload.target] || payload.target;

    const resetReview = () => {
        runIdRef.current += 1;
        setReview({ stage: "idle" });
        setCompareOpen(false);
        setSaveState(null);
    };

    const decideRow = (key, decision) => {
        resetReview();
        setDecisions((prev) => {
            const next = new Map(prev);
            if (decision) next.set(key, decision);
            else next.delete(key);
            return next;
        });
    };

    const decideAll = (decision) => {
        resetReview();
        setDecisions(new Map(changeKeys.map((key) => [key, decision])));
    };
    const acceptAll = () => decideAll("accepted");
    const declineAll = () => decideAll("declined");

    const reviewedText = useMemo(
        () => buildReviewedText(payload.diff, decisions),
        [payload.diff, decisions],
    );
    const busy = review.stage === "checking" || review.stage === "testing";

    const testNoteBlocker = !isCapabilityEnabled("testNote")
        ? "Test Note is not available in this environment."
        : !payload.template_version
          ? "The stored prompt version is unknown, so a test note can't be pinned to it."
          : null;

    const handleCheck = async () => {
        if (!canApply || busy) return;
        const runId = ++runIdRef.current;
        setFullViewOpen(false);
        setSaveState(null);
        setReview({ stage: "checking" });

        const result = await checkTemplateGuardrails({
            name: payload.template_name,
            version: payload.template_version,
            newText: reviewedText,
            requestedChange: payload.requested_change,
        });
        if (runIdRef.current !== runId) return;

        if (result.error) {
            setReview({ stage: "check_error", error: result.error });
        } else {
            setReview({
                stage: result.decision.accepted ? "accepted" : "rejected",
                decision: result.decision,
            });
        }
    };

    const handleRunTestNotesClick = () => {
        if (patientId) {
            handleRunTestNotes(patientId);
        } else {
            setPatientPromptOpen(true);
        }
    };

    const handlePatientSubmit = (event) => {
        event.preventDefault();
        const id = enteredPatientId.trim();
        if (!id) return;
        setPatientPromptOpen(false);
        handleRunTestNotes(id);
    };

    const handleRunTestNotes = async (testPatientId) => {
        if (busy || testNoteBlocker || !testPatientId || !review.decision?.accepted) {
            return;
        }
        const runId = ++runIdRef.current;
        const { decision } = review;
        const isActive = () => runIdRef.current === runId;
        const failTests = (error) =>
            setReview({ stage: "test_error", decision, error });

        setReview({ stage: "testing", decision, progress: "Creating the draft version…" });

        let draft = draftRef.current?.text === reviewedText ? draftRef.current : null;
        if (!draft) {
            const created = await createTemplateDraft({
                name: payload.template_name,
                baseVersion: payload.template_version,
                text: reviewedText,
            });
            if (!isActive()) return;
            if (created.error) return failTests(created.error);
            draft = { ...created.draft, text: reviewedText };
            draftRef.current = draft;
        }

        setReview({ stage: "testing", decision });
        const [current, suggested] = await Promise.all([
            runTestNoteWithPrompt({
                patientId: testPatientId,
                name: payload.template_name,
                version: payload.template_version,
                isActive,
            }),
            runTestNoteWithPrompt({
                patientId: testPatientId,
                name: payload.template_name,
                version: draft.version,
                isActive,
            }),
        ]);
        if (!isActive() || current.abandoned || suggested.abandoned) return;
        if (current.error) return failTests(`Current prompt: ${current.error}`);
        if (suggested.error) return failTests(`Suggested prompt: ${suggested.error}`);

        setReview({
            stage: "tested",
            decision,
            draft,
            patientId: testPatientId,
            current: { ...current, version: payload.template_version },
            suggested: { ...suggested, version: draft.version },
        });
        setCompareOpen(true);
    };

    const handleSave = async () => {
        if (review.stage !== "tested" || saveState || locked) return;
        setSaveState("saving");
        const result = await saveTemplateEdit({
            name: payload.template_name,
            draftVersion: review.draft.version,
            baseVersion: payload.template_version,
        });
        if (result.error) {
            setSaveState(null);
            toast({
                variant: "destructive",
                title: "Could not save the template",
                description: result.error,
            });
            return;
        }
        setSaveState("saved");
        toast({
            title: "Template saved",
            description: `Created ${result.version} and made it the default for ${payload.template_name}. To roll back, set ${result.previousVersion} as the default on the Templates page.`,
        });
    };

    const diffReview = (collapseContext) => (
        <DiffReview
            blocks={blocks}
            decisions={decisions}
            onDecide={decideRow}
            collapseContext={collapseContext}
        />
    );

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-xs">
                <MetaChip label={targetLabel}>
                    <a
                        href={templatePath(payload.template_name)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono font-medium text-ktx-purple hover:underline break-all"
                        title="Open in Templates page"
                    >
                        {payload.template_name}
                    </a>
                </MetaChip>
                {payload.template_version && (
                    <MetaChip label="Version">{payload.template_version}</MetaChip>
                )}
                {payload.specialty && (
                    <MetaChip label="Specialty">{payload.specialty}</MetaChip>
                )}
            </div>

            <ProviderImpact
                impact={payload.provider_impact}
                specialty={payload.specialty}
                target={payload.target}
            />

            {editAccess.status === "error" && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                    <AlertTriangle
                        className="h-4 w-4 shrink-0 mt-0.5"
                        aria-hidden
                    />
                    <span className="flex-1">
                        {editAccess.message} Checking, testing, and saving stay
                        disabled until access is confirmed.
                    </span>
                    <button
                        type="button"
                        onClick={loadEditAccess}
                        disabled={editAccess.retrying}
                        className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900 disabled:opacity-50 shrink-0"
                    >
                        {editAccess.retrying ? (
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        ) : (
                            <RefreshCw className="h-3 w-3" aria-hidden />
                        )}
                        Retry
                    </button>
                </div>
            )}

            {editAccess.status === "denied" && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                    <AlertTriangle
                        className="h-4 w-4 shrink-0 mt-0.5"
                        aria-hidden
                    />
                    <span>
                        Template edits are limited to approved admins. You can
                        view this recommendation, but you cannot check, test,
                        or save it.
                    </span>
                </p>
            )}

            {defaultMoved && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                    <AlertTriangle
                        className="h-4 w-4 shrink-0 mt-0.5"
                        aria-hidden
                    />
                    <span>{payload.diff_error}</span>
                </p>
            )}

            {payload.diff_error && !defaultMoved && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                    <AlertTriangle
                        className="h-4 w-4 shrink-0 mt-0.5"
                        aria-hidden
                    />
                    <span>
                        Could not compare against the stored template:{" "}
                        {payload.diff_error} The agent&apos;s proposal is shown
                        below for manual review.
                    </span>
                </p>
            )}

            {defaultMoved ? null : totalChanges > 0 ? (
                <div className={`${supportInsetPanelAuto} overflow-hidden`}>
                    <div
                        className={`${supportSubpanelHeader} flex flex-wrap items-center justify-between gap-2`}
                    >
                        <h4 className={supportContextCardTitle}>
                            Proposed changes{" "}
                            <DecisionCount
                                accepted={acceptedChanges}
                                total={totalChanges}
                            />
                        </h4>
                        <div className="flex items-center gap-3">
                            <BulkDecisionButtons
                                onAcceptAll={acceptAll}
                                onDeclineAll={declineAll}
                            />
                            <button
                                type="button"
                                onClick={() => setFullViewOpen(true)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-ktx-purple hover:underline"
                            >
                                <Maximize2 className="h-3 w-3" aria-hidden />
                                View full template
                            </button>
                        </div>
                    </div>
                    <div className="max-h-[28rem] overflow-y-auto">
                        {diffReview(true)}
                    </div>
                </div>
            ) : payload.diff ? (
                <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                    The proposed template text is identical to the stored
                    version — there is nothing to apply.
                </p>
            ) : payload.updated_text ? (
                <div className={`${supportInsetPanelAuto} overflow-hidden`}>
                    <div className={supportSubpanelHeader}>
                        <h4 className={supportContextCardTitle}>
                            Proposed template text
                        </h4>
                    </div>
                    <pre className="px-3 py-2.5 max-h-[28rem] overflow-auto font-mono text-xs leading-relaxed text-gray-800 whitespace-pre-wrap break-words">
                        {payload.updated_text}
                    </pre>
                </div>
            ) : (
                Array.isArray(payload.edits) &&
                payload.edits.length > 0 && (
                    <div className={`${supportInsetPanelAuto} overflow-hidden`}>
                        <div className={supportSubpanelHeader}>
                            <h4 className={supportContextCardTitle}>
                                Proposed edits
                            </h4>
                        </div>
                        <div className="max-h-[28rem] overflow-auto divide-y divide-gray-100">
                            {payload.edits.map((edit, index) => (
                                <div key={index} className="px-3 py-2.5 space-y-1.5">
                                    <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words text-red-800 bg-red-50 rounded px-2 py-1">
                                        {edit?.find}
                                    </pre>
                                    <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap break-words text-green-800 bg-green-50 rounded px-2 py-1">
                                        {edit?.replace}
                                    </pre>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}

            {defaultMoved && (
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        type="button"
                        disabled
                        className="bg-ktx-purple hover:bg-purple-700 text-white"
                    >
                        <ShieldCheck className="h-4 w-4" aria-hidden />
                        Check guardrails
                    </Button>
                    <Button
                        type="button"
                        disabled
                        className="bg-ktx-purple hover:bg-purple-700 text-white"
                    >
                        Save template
                    </Button>
                </div>
            )}

            {totalChanges > 0 && (
                <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            onClick={handleCheck}
                            disabled={!canApply || busy}
                            className="bg-ktx-purple hover:bg-purple-700 text-white"
                        >
                            {review.stage === "checking" ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                                <ShieldCheck className="h-4 w-4" aria-hidden />
                            )}
                            {review.stage === "checking"
                                ? "Checking guardrails…"
                                : "Check guardrails"}
                        </Button>
                        {["accepted", "testing", "test_error"].includes(review.stage) && (
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleRunTestNotesClick}
                                disabled={busy || Boolean(testNoteBlocker) || patientPromptOpen}
                                className="border-ktx-purple text-ktx-purple hover:bg-purple-50"
                            >
                                {review.stage === "testing" ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                ) : (
                                    <FlaskConical className="h-4 w-4" aria-hidden />
                                )}
                                {review.stage === "testing"
                                    ? "Generating test notes…"
                                    : "Run test notes"}
                            </Button>
                        )}
                        {review.stage === "tested" && (
                            <>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setCompareOpen(true)}
                                    className="border-ktx-purple text-ktx-purple hover:bg-purple-50"
                                >
                                    <GitCompare className="h-4 w-4" aria-hidden />
                                    Compare test notes
                                </Button>
                                <Button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={Boolean(saveState) || locked}
                                    className="bg-ktx-purple hover:bg-purple-700 text-white"
                                >
                                    {saveState === "saving" && (
                                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                    )}
                                    {saveState === "saved" && (
                                        <Check className="h-4 w-4" aria-hidden />
                                    )}
                                    {saveState === "saved" ? "Template saved" : "Save template"}
                                </Button>
                            </>
                        )}
                    </div>

                    <GuardrailResult review={review} />

                    {patientPromptOpen &&
                        ["accepted", "test_error"].includes(review.stage) && (
                            <form
                                onSubmit={handlePatientSubmit}
                                className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 space-y-2"
                            >
                                <label
                                    htmlFor="template-test-patient-id"
                                    className="block text-xs font-medium text-gray-700"
                                >
                                    This ticket has no patient. Enter a patient ID
                                    to generate the test notes from their latest
                                    visit.
                                </label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        id="template-test-patient-id"
                                        value={enteredPatientId}
                                        onChange={(event) =>
                                            setEnteredPatientId(event.target.value)
                                        }
                                        placeholder="Patient ID"
                                        autoFocus
                                        className="h-9 bg-white"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={!enteredPatientId.trim()}
                                        className="bg-ktx-purple hover:bg-purple-700 text-white"
                                    >
                                        Run
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={() => setPatientPromptOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        )}

                    {review.stage === "accepted" && testNoteBlocker && (
                        <p className="text-xs text-amber-700">{testNoteBlocker}</p>
                    )}
                    {review.stage === "testing" && review.progress && (
                        <p className="text-xs text-gray-500">{review.progress}</p>
                    )}
                    {review.stage === "test_error" && (
                        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex items-start gap-1.5">
                            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
                            <span>Test notes failed: {review.error}</span>
                        </p>
                    )}
                </div>
            )}

            {review.stage === "tested" && (
                <TemplateTestNoteCompareModal
                    open={compareOpen}
                    onOpenChange={setCompareOpen}
                    patientId={review.patientId}
                    templateName={payload.template_name}
                    current={review.current}
                    suggested={review.suggested}
                    saving={saveState === "saving"}
                    saved={saveState === "saved"}
                    onSave={handleSave}
                />
            )}

            {/* Same decisions, uncollapsed: the whole template end to end. */}
            <Dialog open={fullViewOpen} onOpenChange={setFullViewOpen}>
                <DialogContent className="w-[95vw] max-w-[1200px] h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-semibold text-gray-900">
                            {payload.template_name}
                        </DialogTitle>
                        <DialogDescription className="text-base text-gray-700">
                            {targetLabel}
                            {payload.template_version
                                ? ` · Version ${payload.template_version}`
                                : ""}
                            {payload.specialty ? ` · ${payload.specialty}` : ""}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 pb-2">
                        <span className="text-sm font-semibold text-slate-800">
                            Proposed changes{" "}
                            <DecisionCount
                                accepted={acceptedChanges}
                                total={totalChanges}
                            />
                        </span>
                        <BulkDecisionButtons
                            onAcceptAll={acceptAll}
                            onDeclineAll={declineAll}
                        />
                    </div>

                    <div className="flex-1 min-h-0 overflow-y-auto rounded-md border border-gray-300 bg-white">
                        {diffReview(false)}
                    </div>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => setFullViewOpen(false)}
                            className="border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                        >
                            Close
                        </Button>
                        <Button
                            type="button"
                            onClick={handleCheck}
                            disabled={!canApply || busy}
                            className="bg-ktx-purple hover:bg-purple-700 text-white"
                        >
                            Check guardrails
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UpdateTemplateAction;
